// اعتبار — the credit screen's data layer.
//
// FOUR hooks in one file rather than four files, because they are one subject
// and three of them are two dozen lines. They are separate hooks and not one
// combined query on purpose: the balance is polled from several places and
// refetched on events, while the ledger is paged and filtered — a single
// `useCredits()` would refetch a 30-row ledger every time a visit ended.
//
// No `ownerId` is sent anywhere below. The BFF scopes every one of these to the
// caller's own token (parsina.bff.dentist/src/graphql/typeDefs/credits.js), so
// there is nothing here for a client to get wrong or to tamper with.

import React from 'react';
import { bffQuery, bffMutation } from '../lib/bffClient.js';
import { coinPriceToman } from '../lib/useCreditPrices.js';
import { PAYMENT_GATEWAY, toGatewayAmount } from '../constants/paymentGateway.js';

// ── Balance ─────────────────────────────────────────────────────────────────

const BALANCE_QUERY = `
  query MyCreditBalance {
    myCreditBalance { balance currency }
  }
`;

/**
 * Broadcast that the balance has moved.
 *
 * Anything that changes it — a redeemed gift code, a settled top-up — fires
 * this and every mounted copy of `useCreditBalance` refetches. An event rather
 * than a returned `refetch` because the balance is read in more than one place
 * at once (the hero and the KPI strip on this screen; the sidebar later), and
 * threading a refetch through all of them is how two of them come to disagree
 * about the same number.
 */
export const CREDITS_CHANGED = 'parsina:credits-changed';

export function notifyCreditsChanged() {
  if (typeof window !== 'undefined') window.dispatchEvent(new Event(CREDITS_CHANGED));
}

/** The dentist's own balance, or null while it is unknown. */
export function useCreditBalance() {
  const [balance, setBalance] = React.useState(null);
  const [error, setError] = React.useState(null);

  const load = React.useCallback(() => {
    bffQuery(BALANCE_QUERY)
      .then((data) => {
        setBalance(data?.myCreditBalance?.balance ?? null);
        setError(null);
      })
      // A failed balance read leaves the previous number on screen rather than
      // blanking it. `null` here does not mean zero — it means unknown — and
      // painting «۰ پارسه» over a working wallet is the one wrong answer.
      .catch((err) => setError(err));
  }, []);

  React.useEffect(() => { load(); }, [load]);

  // Something in this tab moved it.
  React.useEffect(() => {
    window.addEventListener(CREDITS_CHANGED, load);
    return () => window.removeEventListener(CREDITS_CHANGED, load);
  }, [load]);

  // The dentist came back to the tab — most likely from the payment gateway, or
  // from the doctor panel, where the SAME wallet may have moved.
  React.useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') load();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [load]);

  return { balance, error, reload: load };
}

// ── Ledger ──────────────────────────────────────────────────────────────────

const TRANSACTIONS_QUERY = `
  query MyCreditTransactions(
    $limit: Int, $offset: Int, $startDate: String, $endDate: String, $type: String
  ) {
    myCreditTransactions(
      limit: $limit, offset: $offset, startDate: $startDate, endDate: $endDate, type: $type
    ) {
      totalCount
      transactions { id type amount serviceType description createdAt referenceId }
    }
  }
`;

/**
 * One page of the ledger.
 *
 * Every filter is passed to the SERVER. The ledger runs to thousands of rows,
 * so a filter applied to the fetched page would show only the matches that
 * happened to land on it, underneath a `totalCount` describing the whole set.
 */
export function useCreditTransactions({
  limit = 30, offset = 0, startDate = '', endDate = '', type = '',
} = {}) {
  const [data, setData] = React.useState({ transactions: [], totalCount: 0 });
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  const load = React.useCallback(() => {
    setLoading(true);
    bffQuery(TRANSACTIONS_QUERY, { limit, offset, startDate, endDate, type })
      .then((res) => {
        setData(res?.myCreditTransactions ?? { transactions: [], totalCount: 0 });
        setError(null);
      })
      .catch((err) => setError(err))
      .finally(() => setLoading(false));
  }, [limit, offset, startDate, endDate, type]);

  React.useEffect(() => { load(); }, [load]);

  return { ...data, loading, error, reload: load };
}

// ── Top-up ──────────────────────────────────────────────────────────────────

const TOPUP_MUTATION = `
  mutation StartCreditTopUp($gateway: String!, $amount: Int!, $description: String, $returnUrl: String) {
    startCreditTopUp(gateway: $gateway, amount: $amount, description: $description, returnUrl: $returnUrl) {
      gateway paymentUrl
    }
  }
`;

/**
 * Begin a top-up: ask the BFF for a payment URL, then send the browser to it.
 *
 * What happens next is NOT this app's business. The gateway reports
 * server-to-server, the payment BFF settles with the bank and credits the
 * wallet, and the browser is sent back to `returnUrl` with `?payment=success`
 * or `?payment=failed`. This client could not settle a payment if it tried —
 * the bank's receipt never reaches it.
 */
export function useTopUp(gateway = PAYMENT_GATEWAY) {
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);

  const startTopUp = React.useCallback(
    async (coins, toman, { description, returnUrl } = {}) => {
      setBusy(true);
      setError(null);
      try {
        const amountToman = Math.round(toman ?? coins * coinPriceToman());
        const res = await bffMutation(TOPUP_MUTATION, {
          gateway,
          // The one conversion. Sepehr settles in rials, Zarinpal in toman.
          amount: toGatewayAmount(amountToman, gateway),
          description: description || '',
          returnUrl: returnUrl || `${window.location.origin}/credits`,
        });
        const url = res?.startCreditTopUp?.paymentUrl;
        if (url) {
          window.location.href = url;
          // Deliberately not clearing `busy`: the page is navigating away, and
          // re-enabling the button for the last frames invites a second charge.
          return true;
        }
        setError(new Error('NO_URL'));
        return false;
      } catch (err) {
        // The server's own message is shown when there is one. A gateway that
        // refused the amount, a wallet that is down and a network blip are three
        // different instructions to the dentist, and «به‌زودی» over all of them
        // says a working feature does not exist.
        setError(err);
        return false;
      } finally {
        setBusy(false);
      }
    },
    [gateway],
  );

  return { startTopUp, busy, error };
}

// ── Low-balance alert ───────────────────────────────────────────────────────

const LOW_BALANCE_QUERY = `
  query MyLowBalanceConfig {
    myLowBalanceConfig { threshold enableAlerts }
  }
`;

const LOW_BALANCE_MUTATION = `
  mutation UpdateMyLowBalanceThreshold($threshold: Float!, $enableAlerts: Boolean!) {
    updateMyLowBalanceThreshold(threshold: $threshold, enableAlerts: $enableAlerts) {
      threshold enableAlerts
    }
  }
`;

/**
 * The wallet's own low-balance warning. Distinct from the red floor the page
 * paints below 50 پارسه (src/constants/credit.js): that one is a client rule
 * about colour, this one is what the wallet actually alerts on.
 */
export function useLowBalanceConfig() {
  const [config, setConfig] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState(null);

  const load = React.useCallback(() => {
    setLoading(true);
    bffQuery(LOW_BALANCE_QUERY)
      .then((res) => {
        const c = res?.myLowBalanceConfig;
        setConfig(c ? { threshold: c.threshold, enableAlerts: c.enableAlerts } : null);
        setError(null);
      })
      .catch((err) => setError(err))
      .finally(() => setLoading(false));
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const save = React.useCallback(async (next) => {
    setSaving(true);
    try {
      const res = await bffMutation(LOW_BALANCE_MUTATION, {
        threshold: Number(next.threshold),
        enableAlerts: !!next.enableAlerts,
      });
      const c = res?.updateMyLowBalanceThreshold;
      setConfig({ threshold: c.threshold, enableAlerts: c.enableAlerts });
      return true;
    } catch (err) {
      setError(err);
      return false;
    } finally {
      setSaving(false);
    }
  }, []);

  return { config, loading, saving, error, reload: load, save };
}

// ── Gift codes ──────────────────────────────────────────────────────────────

const MY_GIFT_CODES = `
  query MyGiftCodes {
    myGiftCodes { id code parseh smartVisits status expiresAtMs redeemedAtMs }
  }
`;

const REDEEM_GIFT_CODE = `
  mutation RedeemGiftCode($code: String!) {
    redeemGiftCode(code: $code) {
      granted
      giftCode { id code parseh smartVisits status redeemedAtMs }
      balance { balance currency }
    }
  }
`;

/**
 * The dentist's gift codes, and the one action that spends them.
 *
 * `redeem` RESOLVES with the outcome rather than throwing on a rejected code: a
 * wrong code is an ordinary result of this form, not an exception, and the card
 * renders it as a field message. Only the shape of the result says which.
 */
export function useGiftCodes() {
  const [codes, setCodes] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  const refresh = React.useCallback(() => {
    setLoading(true);
    bffQuery(MY_GIFT_CODES)
      .then((data) => setCodes(data?.myGiftCodes || []))
      // The BFF already answers `[]` for an unreachable CRM; this catch is for
      // a failed request, and the answer is the same — the rest of the page
      // works, and the dentist is not shown an error about a card they may
      // never use.
      .catch(() => setCodes([]))
      .finally(() => setLoading(false));
  }, []);

  React.useEffect(() => { refresh(); }, [refresh]);

  const redeem = React.useCallback(async (code) => {
    const trimmed = (code || '').trim();
    if (!trimmed) return { ok: false, code: 'EMPTY' };
    try {
      const data = await bffMutation(REDEEM_GIFT_CODE, { code: trimmed });
      const result = data?.redeemGiftCode;
      if (!result) return { ok: false, code: 'UNKNOWN' };
      refresh();
      return {
        ok: true,
        granted: result.granted,
        smartVisits: result.giftCode?.smartVisits ?? 0,
        balance: result.balance?.balance ?? null,
      };
    } catch (err) {
      // The BFF's message is written FOR the dentist — «قبلاً استفاده شده»,
      // «منقضی شده» — and each is a different instruction, so it is shown rather
      // than replaced with one generic line.
      const gqlError = err?.graphQLErrors?.[0];
      return {
        ok: false,
        code: gqlError?.extensions?.code || err?.extensions?.code || 'UNKNOWN',
        message: gqlError?.message || err?.message || '',
      };
    }
  }, [refresh]);

  return { codes, loading, redeem, refresh };
}
